/**
 * <testimonial-carousel>
 *
 * Crossfade testimonial carousel. Slides are stacked in a single CSS grid cell
 * and toggled via the `is-active` class, so changing slide is a pure opacity
 * fade with no layout shift. Supports autoplay (pauses on hover/focus and when
 * offscreen), prev/next arrows, dot navigation, swipe, and keyboard.
 *
 * Markup contract (rendered by sections/testimonials-carousel.liquid):
 *   data-autoplay  -> presence enables autoplay
 *   data-speed     -> autoplay interval in ms
 *   [data-slide]   -> each review, with [data-index]
 *   [data-dot]     -> dot button, with [data-index]
 *   [data-prev] / [data-next] -> arrow buttons
 *   [data-track]   -> swipe surface
 */
class TestimonialCarousel extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-slide]'));
    this.dots = Array.from(this.querySelectorAll('[data-dot]'));
    this.track = this.querySelector('[data-track]');

    if (this.slides.length <= 1) return;

    this.index = this.slides.findIndex((s) => s.classList.contains('is-active'));
    if (this.index < 0) this.index = 0;

    this.autoplay = this.hasAttribute('data-autoplay');
    this.speed = parseInt(this.getAttribute('data-speed'), 10) || 6000;
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.timer = null;

    this.#bindControls();
    this.#bindAutoplay();
    this.#bindSwipe();
    this.#bindKeyboard();

    this.goTo(this.index);
    this.start();
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
  }

  goTo(next) {
    const count = this.slides.length;
    this.index = (next + count) % count;

    this.slides.forEach((slide, i) => {
      const active = i === this.index;
      slide.classList.toggle('is-active', active);
      slide.toggleAttribute('aria-hidden', !active);
    });

    this.dots.forEach((dot, i) => {
      const active = i === this.index;
      dot.classList.toggle('is-active', active);
      dot.setAttribute('aria-selected', active ? 'true' : 'false');
    });
  }

  next() {
    this.goTo(this.index + 1);
  }

  prev() {
    this.goTo(this.index - 1);
  }

  /** Advance, then reset the autoplay clock so manual input feels responsive. */
  #advance(dir) {
    dir > 0 ? this.next() : this.prev();
    this.restart();
  }

  start() {
    if (!this.autoplay || this.reducedMotion || this.timer || this.paused) return;
    this.timer = window.setInterval(() => this.next(), this.speed);
  }

  stop() {
    window.clearInterval(this.timer);
    this.timer = null;
  }

  restart() {
    this.stop();
    this.start();
  }

  #bindControls() {
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.#advance(-1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.#advance(1));
    this.dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        this.goTo(parseInt(dot.dataset.index, 10) || 0);
        this.restart();
      });
    });
  }

  #bindAutoplay() {
    // Pause while the visitor is hovering or focused within the carousel.
    const pause = () => {
      this.paused = true;
      this.stop();
    };
    const resume = () => {
      this.paused = false;
      this.start();
    };

    this.addEventListener('pointerenter', pause);
    this.addEventListener('pointerleave', resume);
    this.addEventListener('focusin', pause);
    this.addEventListener('focusout', resume);

    // Stop the timer while offscreen to avoid silent background cycling.
    this.observer = new IntersectionObserver(
      ([entry]) => (entry.isIntersecting ? this.start() : this.stop()),
      { threshold: 0.1 }
    );
    this.observer.observe(this);
  }

  #bindSwipe() {
    if (!this.track) return;
    let startX = 0;
    let active = false;

    this.track.addEventListener('pointerdown', (e) => {
      active = true;
      startX = e.clientX;
    });

    this.track.addEventListener('pointerup', (e) => {
      if (!active) return;
      active = false;
      const dx = e.clientX - startX;
      if (Math.abs(dx) > 40) this.#advance(dx < 0 ? 1 : -1);
    });

    this.track.addEventListener('pointercancel', () => {
      active = false;
    });
  }

  #bindKeyboard() {
    this.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowRight') this.#advance(1);
      else if (e.key === 'ArrowLeft') this.#advance(-1);
    });
  }
}

if (!customElements.get('testimonial-carousel')) {
  customElements.define('testimonial-carousel', TestimonialCarousel);
}
